import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  TextField,
  Checkbox,
  Toolbar,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { cmi5ConfigService, RC5AU, RC5Block } from '../services/cmi5-config';
import { moodleApi } from '../services/moodle-api';
import { rangeosApiService } from '../services/rangeos-api';
import { rangeosEnvironmentService, RangeOSEnvironment } from '../services/rangeos-environment-api';
import { s3BucketsApi } from '../services/s3-buckets-api';
import { ScenarioSelectionDialog } from './ScenarioSelectionDialog';
import { S3ExportDialog } from './S3ExportDialog';
import { S3BatchExportDialog } from './S3BatchExportDialog';
import { AUScenarioLinkageDisplay } from './AUScenarioLinkageDisplay';
import { AUScenarioLinkageEditDialog } from './AUScenarioLinkageEditDialog';

interface CMI5Activity {
  tenantId: string;
  courseId: string;
  activityId: number;
  activityName: string;
  cmi5activityid?: string;
  moodleCourseId?: number;
  size?: number;
  lastModified?: string;
  versionInfo?: {
    currentVersion: string;
    totalVersions: number;
    hasVersionHistory: boolean;
  };
}

interface CfgLocation {
  name: string;
  url: string;
  config: {
    DEVOPS_API_URL: string;
    DEVOPS_GQL_URL?: string;
    DEVOPS_GQL_SUBSCRIPTIONS_URL?: string;
    SLIDE_BACKGROUND?: string;
    SLIDE_LOGO?: string;
  };
}

interface CfgJson {
  locations: CfgLocation[];
  [key: string]: any;
}

interface CfgValidation {
  exists: boolean;
  hasEnvironment: boolean;
  environmentMatches: boolean;
  configuredLocationName?: string;
  issues?: string[];
}

interface CMI5ActivityBrowserDialogProps {
  open: boolean;
  onClose: () => void;
  sourceId: string;
  sourceName: string;
  activities: CMI5Activity[];
  sourceEnvironmentId?: string; // The source's configured RangeOS environment
  sourceType?: 'moodle' | 's3'; // Type of source (defaults to 'moodle' for backwards compatibility)
  s3BucketId?: string; // For S3 sources: the bucket ID
  s3EnvironmentId?: string; // For S3 sources: the environment ID
}

export const CMI5ActivityBrowserDialog: React.FC<CMI5ActivityBrowserDialogProps> = ({
  open,
  onClose,
  sourceId,
  sourceName,
  activities,
  sourceEnvironmentId,
  sourceType = 'moodle',
  s3BucketId,
  s3EnvironmentId,
}) => {
  console.log('[CMI5Browser] Component render - props:', {
    open,
    sourceId,
    sourceName,
    sourceType,
    s3BucketId,
    s3EnvironmentId,
  });

  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activityConfigs, setActivityConfigs] = useState<Map<string, { aus: RC5AU[]; blocks?: RC5Block[] }>>(new Map());
  const [loadingConfigs, setLoadingConfigs] = useState<Set<string>>(new Set());
  const [editingAU, setEditingAU] = useState<string | null>(null);  // Individual AU being edited
  const [environments, setEnvironments] = useState<RangeOSEnvironment[]>([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('');
  const [scenarioDialogOpen, setScenarioDialogOpen] = useState(false);
  const [scenarioDialogActivity, setScenarioDialogActivity] = useState<CMI5Activity | null>(null);
  const [scenarioDialogAu, setScenarioDialogAu] = useState<RC5AU | null>(null);
  const [scenarioDialogEnvironment, setScenarioDialogEnvironment] = useState<string | null>(null);
  // Mapping matrix: activityKey -> environmentId -> auId -> boolean (has mapping)
  const [mappingMatrix, setMappingMatrix] = useState<Map<string, Map<string, Map<string, boolean>>>>(new Map());
  // Scenario availability: activityKey -> environmentId -> auId -> boolean (scenario exists in that env)
  const [scenarioAvailability, setScenarioAvailability] = useState<Map<string, Map<string, Map<string, boolean>>>>(new Map());
  // cfg.json validation: activityKey -> validation result
  const [cfgValidations, setCfgValidations] = useState<Map<string, CfgValidation>>(new Map());
  // For Moodle sources: track the S3 bucket ID that backs this Moodle instance
  const [moodleS3BucketId, setMoodleS3BucketId] = useState<string | undefined>(undefined);
  // cfg.json data: activityKey -> cfg.json data
  const [cfgDataCache, setCfgDataCache] = useState<Map<string, CfgJson>>(new Map());
  // cfg.json editor state
  const [cfgEditorOpen, setCfgEditorOpen] = useState(false);
  const [cfgEditorActivity, setCfgEditorActivity] = useState<CMI5Activity | null>(null);
  const [cfgEditorBlock, setCfgEditorBlock] = useState<string | null>(null); // Which block is being edited
  const [cfgEditorData, setCfgEditorData] = useState<string>('');
  const [cfgEditorError, setCfgEditorError] = useState<string | null>(null);
  const [cfgEditorEnvironment, setCfgEditorEnvironment] = useState<RangeOSEnvironment | null>(null); // If set, edit only this environment's stanza
  const [cfgEditorShowDiff, setCfgEditorShowDiff] = useState(false); // Show side-by-side diff
  const [cfgEditorExpectedData, setCfgEditorExpectedData] = useState<string>(''); // Expected values for diff
  // S3 upload state
  const [uploadingPackages, setUploadingPackages] = useState<Set<string>>(new Set());
  // Scenario definition export/import state
  const [savedScenarioDefinitions, setSavedScenarioDefinitions] = useState<Set<string>>(new Set()); // auIds that have saved definitions
  const [exportingScenarios, setExportingScenarios] = useState<Set<string>>(new Set()); // auIds currently being exported
  const [importingScenarios, setImportingScenarios] = useState<Set<string>>(new Set()); // auIds currently being imported
  // S3 export dialog state
  const [s3ExportDialogOpen, setS3ExportDialogOpen] = useState(false);
  const [s3ExportActivity, setS3ExportActivity] = useState<CMI5Activity | null>(null);
  // Multi-select and batch export state
  const [selectedActivities, setSelectedActivities] = useState<Set<string>>(new Set());
  const [batchExportDialogOpen, setBatchExportDialogOpen] = useState(false);
  // AU-Scenario linkage edit dialog state
  const [linkageEditDialogOpen, setLinkageEditDialogOpen] = useState(false);
  const [linkageEditActivity, setLinkageEditActivity] = useState<CMI5Activity | null>(null);
  const [linkageEditAU, setLinkageEditAU] = useState<RC5AU | null>(null);
  // Validation state
  const [validating, setValidating] = useState(false);
  const [validationResults, setValidationResults] = useState<Map<string, any>>(new Map());
  const [showValidation, setShowValidation] = useState(false);

  // Course name lookup for grouping
  const [courseNames, setCourseNames] = useState<Map<number, string>>(new Map());

  // Group activities by moodleCourseId
  const groupedActivities = useMemo(() => {
    const groups = new Map<number, CMI5Activity[]>();
    activities.forEach(activity => {
      const courseId = activity.moodleCourseId || 0;
      if (!groups.has(courseId)) {
        groups.set(courseId, []);
      }
      groups.get(courseId)!.push(activity);
    });
    return groups;
  }, [activities]);

  // Helper function to get activity key
  const getActivityKey = (activity: CMI5Activity) => {
    return `${activity.tenantId}-${activity.courseId}`;
  };

  // Helper function to get cfg cache key (per-block for multi-block activities)
  const getCfgCacheKey = (activity: CMI5Activity, blockName?: string): string => {
    const activityKey = getActivityKey(activity);
    return blockName ? `${activityKey}::${blockName}` : activityKey;
  };

  // Extract the first block name from an activity's config (for block-level cfg.json reads)
  const getFirstBlockName = (activity: CMI5Activity): string | undefined => {
    const activityKey = getActivityKey(activity);
    const config = activityConfigs.get(activityKey);
    if (config?.blocks && config.blocks.length > 0) {
      return config.blocks[0].blockName;
    }
    return undefined;
  };

  // Fetch Moodle S3 bucket configuration
  const fetchMoodleS3Bucket = useCallback(async () => {
    console.log('[CMI5Browser] fetchMoodleS3Bucket called - sourceType:', sourceType, 'sourceId:', sourceId);
    if (sourceType !== 'moodle') {
      console.log('[CMI5Browser] Skipping fetchMoodleS3Bucket - source type is not moodle');
      return;
    }

    try {
      console.log('[CMI5Browser] Fetching Moodle source to get S3 bucket:', sourceId);
      // Fetch the Moodle source to get its S3 bucket name
      const { moodleApi } = await import('../services/moodle-api');
      console.log('[CMI5Browser] moodleApi imported, calling getSource...');

      const moodleSource = await moodleApi.getSource(sourceId);
      console.log('[CMI5Browser] Moodle source retrieved:', JSON.stringify(moodleSource, null, 2));
      console.log('[CMI5Browser] Moodle source awsS3Bucket field:', moodleSource.awsS3Bucket);

      if (moodleSource.awsS3Bucket) {
        console.log('[CMI5Browser] Moodle source uses S3 bucket:', moodleSource.awsS3Bucket);

        // Find the S3 bucket configuration with this name
        const { s3BucketsApi } = await import('../services/s3-buckets-api');
        console.log('[CMI5Browser] s3BucketsApi imported, calling listBuckets...');

        const buckets = await s3BucketsApi.listBuckets();
        console.log('[CMI5Browser] Retrieved', buckets.length, 'S3 bucket configurations');
        console.log('[CMI5Browser] Looking for bucket with name:', moodleSource.awsS3Bucket);

        const matchingBucket = buckets.find(b => b.name === moodleSource.awsS3Bucket);

        if (matchingBucket) {
          console.log('[CMI5Browser] Found matching S3 bucket configuration:', matchingBucket.id);
          setMoodleS3BucketId(matchingBucket.id);
        } else {
          console.warn('[CMI5Browser] No S3 bucket configuration found for:', moodleSource.awsS3Bucket);
          console.warn('[CMI5Browser] Available bucket names:', buckets.map(b => b.name).join(', '));
        }
      } else {
        console.warn('[CMI5Browser] Moodle source does NOT have awsS3Bucket configured');
      }

      console.log('[CMI5Browser] fetchMoodleS3Bucket completed successfully');
    } catch (err) {
      console.error('[CMI5Browser] Failed to fetch Moodle S3 bucket:', err);
      console.error('[CMI5Browser] Error details:', err instanceof Error ? err.message : String(err));
    }
  }, [sourceType, sourceId]);

  // Trigger environment loading when dialog opens
  useEffect(() => {
    console.log('[CMI5Browser] useEffect triggered - open:', open);
    if (open) {
      loadEnvironments();
    }
  }, [open]);

  // Auto-load all activity configs when dialog opens
  useEffect(() => {
    if (open && activities.length > 0) {
      activities.forEach(activity => {
        const activityKey = getActivityKey(activity);
        if (!activityConfigs.has(activityKey) && !loadingConfigs.has(activityKey)) {
          loadActivityConfig(activity);
        }
      });
    }
  }, [open, activities]);

  // Fetch S3 bucket for Moodle sources when dialog opens
  useEffect(() => {
    console.log('[CMI5Browser] S3 fetch useEffect - open:', open, 'sourceType:', sourceType);
    if (open && sourceType === 'moodle') {
      console.log('[CMI5Browser] Dialog is open and source type is moodle, calling fetchMoodleS3Bucket');
      fetchMoodleS3Bucket();
    }
  }, [open, sourceType, fetchMoodleS3Bucket]);

  // Fetch course names for grouping (Moodle sources only)
  useEffect(() => {
    if (open && sourceType === 'moodle' && activities.length > 0) {
      moodleApi.listCourses(sourceId).then(courses => {
        const nameMap = new Map<number, string>();
        courses.forEach(c => nameMap.set(c.id, c.fullname));
        setCourseNames(nameMap);
      }).catch(err => {
        console.warn('[CMI5Browser] Failed to load course names:', err.message);
      });
    }
  }, [open, sourceType, sourceId, activities.length]);

  // When environments are loaded, reload mapping matrix for all loaded activities
  useEffect(() => {
    if (environments.length > 0 && activityConfigs.size > 0) {
      console.log('[Environments Loaded] Reloading mapping matrices for', activityConfigs.size, 'activity/activities');
      // Reload mapping matrix for each activity that has a config
      activities.forEach(activity => {
        const activityKey = getActivityKey(activity);
        const config = activityConfigs.get(activityKey);
        if (config) {
          console.log(`[Environments Loaded] Reloading matrix for ${activityKey}`);
          loadMappingMatrix(activity, config);
        }
      });
    }
  }, [environments.length]); // Only trigger when environment count changes

  const loadEnvironments = async () => {
    try {
      console.log('[loadEnvironments] Starting to load environments...');
      console.log('[loadEnvironments] Source environment ID from props:', sourceEnvironmentId);
      const envs = await rangeosEnvironmentService.getActiveEnvironments();
      console.log('[loadEnvironments] Loaded', envs.length, 'environment(s):', envs);
      console.log('[loadEnvironments] Environment IDs:', envs.map(e => e.id));
      console.log('[loadEnvironments] Environment Names:', envs.map(e => e.name));
      console.log('[loadEnvironments] Full environment objects:', JSON.stringify(envs, null, 2));
      setEnvironments(envs);
      console.log('[loadEnvironments] Set environments state with', envs.length, 'environments');

      // Set the selected environment from the source's configuration
      if (sourceEnvironmentId) {
        console.log('[loadEnvironments] Setting selectedEnvironment to source environment:', sourceEnvironmentId);
        setSelectedEnvironment(sourceEnvironmentId);
        console.log('[loadEnvironments] selectedEnvironment state updated');
      } else {
        console.log('[loadEnvironments] No sourceEnvironmentId provided, selectedEnvironment will remain empty');
      }
    } catch (err) {
      console.error('[loadEnvironments] Failed to load environments:', err);
    }
  };

  const loadActivityConfig = async (activity: CMI5Activity, forceReload = false) => {
    const activityKey = `${activity.tenantId}-${activity.courseId}`;

    // Don't reload if already loaded (unless force reload)
    if (!forceReload && activityConfigs.has(activityKey)) {
      return;
    }

    // Don't reload if currently loading
    if (loadingConfigs.has(activityKey)) {
      return;
    }

    setLoadingConfigs(prev => new Set(prev).add(activityKey));
    setError(null);

    try {
      // Fetch AU config (from cmi5.xml + rc5.yaml)
      let config: any;

      if (sourceType === 's3') {
        // For S3 sources, courseId is the S3 key
        const packageKey = activity.courseId;

        if (!s3BucketId || !s3EnvironmentId) {
          throw new Error('S3 bucket and environment IDs are required for S3 sources');
        }

        // Read RC5.yaml (automatically downloads and extracts if needed)
        config = await s3BucketsApi.getRC5Config(s3BucketId, s3EnvironmentId, packageKey);

        // RC5.yaml should contain AU list
        // If not, the backend will have parsed cmi5.xml
      } else {
        // Moodle source - use existing service
        config = await cmi5ConfigService.getRC5Config(
          sourceId,
          activity.tenantId,
          activity.courseId
        );
      }

      const aus = config.au || [];

      // Fetch AU mappings if we have an environment selected
      if (selectedEnvironment && aus.length > 0) {
        try {
          const auIds = aus.map(au => au.id);
          console.log(`Fetching AU mappings for environment ${selectedEnvironment} with ${auIds.length} AU IDs:`, auIds);
          const mappings = await rangeosEnvironmentService.getAUMappings(selectedEnvironment, auIds);
          console.log(`Received ${mappings.length} AU mapping(s) from RangeOS API:`, mappings);

          // Create a map of AU ID to mapping
          const mappingMap = new Map(mappings.map(m => [m.auId, m]));

          // Merge mappings into AUs
          const ausWithMappings = aus.map(au => {
            const mapping = mappingMap.get(au.id);
            if (mapping) {
              console.log(`AU "${au.title}" (${au.id}) has mapping:`, mapping);
              return {
                ...au,
                mappedScenarios: mapping.scenarios,
                mappingName: mapping.name,
              };
            } else {
              console.log(`AU "${au.title}" (${au.id}) has NO mapping`);
            }
            return au;
          });

          // Also update blocks if they exist
          const blocks = config.blocks?.map(block => ({
            ...block,
            aus: block.aus.map(au => {
              const mapping = mappingMap.get(au.id);
              if (mapping) {
                return {
                  ...au,
                  mappedScenarios: mapping.scenarios,
                  mappingName: mapping.name,
                };
              }
              return au;
            }),
          }));

          const finalConfig = { aus: ausWithMappings, blocks };
          setActivityConfigs(prev => new Map(prev).set(activityKey, finalConfig));
          // Load mapping matrix with the config we just created
          await loadMappingMatrix(activity, finalConfig);
          // Validate cfg.json per-block if blocks exist, otherwise activity-level
          await validateCfgJsonForAllBlocks(activity, finalConfig);
        } catch (mappingErr) {
          console.error('Failed to load AU mappings:', mappingErr);
          // Still set the config without mappings
          const finalConfig = { aus, blocks: config.blocks };
          setActivityConfigs(prev => new Map(prev).set(activityKey, finalConfig));
          await loadMappingMatrix(activity, finalConfig);
          await validateCfgJsonForAllBlocks(activity, finalConfig);
        }
      } else {
        const finalConfig = { aus, blocks: config.blocks };
        setActivityConfigs(prev => new Map(prev).set(activityKey, finalConfig));
        await loadMappingMatrix(activity, finalConfig);
        await validateCfgJsonForAllBlocks(activity, finalConfig);
      }
    } catch (err: any) {
      console.error(`Failed to load config for activity ${activity.activityName}:`, err);
      setError(`Failed to load AUs for ${activity.activityName}`);
    } finally {
      setLoadingConfigs(prev => {
        const newSet = new Set(prev);
        newSet.delete(activityKey);
        return newSet;
      });
    }
  };


  const handleAutoMap = async (activity: CMI5Activity, au: RC5AU, environmentId: string) => {
    if (!au.rangeosScenarioName) {
      setError('No scenario name found for this AU');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`[Auto-Map] Searching for scenario "${au.rangeosScenarioName}" in environment ${environmentId}`);

      // Search for scenarios matching the AU's scenario name
      const scenarios = await rangeosEnvironmentService.searchScenarios(environmentId, au.rangeosScenarioName);

      console.log(`[Auto-Map] Found ${scenarios.length} matching scenario(s)`);

      if (scenarios.length === 0) {
        setError(`No scenarios found matching "${au.rangeosScenarioName}"`);
        return;
      }

      // Use the first match (exact or closest match)
      const matchedScenario = scenarios[0];
      console.log(`[Auto-Map] Using scenario:`, matchedScenario);

      // Create the AU mapping
      await rangeosEnvironmentService.saveAUMapping(environmentId, {
        auId: au.id,
        name: au.title,
        scenarios: [matchedScenario.id],
      });

      console.log(`[Auto-Map] Successfully created mapping for AU "${au.title}" to scenario "${matchedScenario.name}"`);

      // Reload the activity config to show the new mapping
      const activityKey = getActivityKey(activity);
      setActivityConfigs(prev => {
        const newMap = new Map(prev);
        newMap.delete(activityKey);
        return newMap;
      });
      await loadActivityConfig(activity, true);

      setSuccessMessage(`Successfully mapped "${au.title}" to "${matchedScenario.name}"`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('[Auto-Map] Failed:', err);
      setError(err.message || 'Failed to create automatic mapping');
    } finally {
      setLoading(false);
    }
  };

  const handleManualMap = (activity: CMI5Activity, au: RC5AU, environmentId: string) => {
    console.log(`[Manual-Map] Opening scenario selection for AU "${au.title}" in environment ${environmentId}`);
    setScenarioDialogActivity(activity);
    setScenarioDialogAu(au);
    setScenarioDialogEnvironment(environmentId);
    setScenarioDialogOpen(true);
  };

  const validateCfgJsonForEnvironment = (cfgData: CfgJson, environment: RangeOSEnvironment): CfgValidation => {
    if (!cfgData.locations || cfgData.locations.length === 0) {
      return {
        exists: true,
        hasEnvironment: false,
        environmentMatches: false,
        issues: ['No locations configured in cfg.json'],
      };
    }

    const issues: string[] = [];
    let matchingLocation: CfgLocation | undefined;
    let hasEnvironment = false;

    // Match by playerUrl (the 'url' field in cfg.json location)
    // This is how the player determines which environment config to use
    if (!environment.playerUrl) {
      return {
        exists: true,
        hasEnvironment: false,
        environmentMatches: false,
        issues: ['Environment has no player URL configured'],
      };
    }

    for (const location of cfgData.locations) {
      // Check if this location matches the environment by comparing player URLs
      if (location.url === environment.playerUrl) {
        matchingLocation = location;
        hasEnvironment = true;

        // Check if all settings match with detailed diff info
        if (environment.devOpsApiUrl && location.config.DEVOPS_API_URL !== environment.devOpsApiUrl) {
          issues.push(`DEVOPS_API_URL: Expected "${environment.devOpsApiUrl}", got "${location.config.DEVOPS_API_URL}"`);
        }

        if (environment.graphqlUrl && location.config.DEVOPS_GQL_URL !== environment.graphqlUrl) {
          issues.push(`DEVOPS_GQL_URL: Expected "${environment.graphqlUrl}", got "${location.config.DEVOPS_GQL_URL || '(empty)'}"`);
        }

        if (environment.graphqlSubscriptionsUrl && location.config.DEVOPS_GQL_SUBSCRIPTIONS_URL !== environment.graphqlSubscriptionsUrl) {
          issues.push(`DEVOPS_GQL_SUBSCRIPTIONS_URL: Expected "${environment.graphqlSubscriptionsUrl}", got "${location.config.DEVOPS_GQL_SUBSCRIPTIONS_URL || '(empty)'}"`);
        }

        break;
      }
    }

    if (!hasEnvironment) {
      return {
        exists: true,
        hasEnvironment: false,
        environmentMatches: false,
        issues: ['Environment not configured in cfg.json'],
      };
    }

    return {
      exists: true,
      hasEnvironment: true,
      environmentMatches: issues.length === 0,
      configuredLocationName: matchingLocation?.name,
      issues: issues.length > 0 ? issues : undefined,
    };
  };

  const validateCfgJson = async (activity: CMI5Activity, blockName?: string): Promise<CfgValidation> => {
    const cacheKey = getCfgCacheKey(activity, blockName);

    try {
      // Fetch cfg.json from the activity
      let cfgData: CfgJson;

      if (sourceType === 's3') {
        const packageKey = activity.courseId;
        if (!s3BucketId || !s3EnvironmentId) {
          throw new Error('S3 bucket and environment IDs are required for S3 sources');
        }
        cfgData = await s3BucketsApi.getCfgJson(s3BucketId, s3EnvironmentId, packageKey);
      } else {
        const block = blockName || getFirstBlockName(activity);
        cfgData = await cmi5ConfigService.getCfgJson(
          sourceId,
          activity.tenantId,
          activity.courseId,
          block
        );
      }

      console.log(`[cfg.json] Loaded for ${cacheKey}:`, cfgData);

      // Cache the cfg.json data
      setCfgDataCache(prev => new Map(prev).set(cacheKey, cfgData));

      if (!cfgData.locations || cfgData.locations.length === 0) {
        return {
          exists: true,
          hasEnvironment: false,
          environmentMatches: false,
          issues: ['No locations configured in cfg.json'],
        };
      }

      // If no environment is selected, just report that cfg.json exists
      if (!selectedEnvironment) {
        return {
          exists: true,
          hasEnvironment: true,
          environmentMatches: false,
          configuredLocationName: cfgData.locations[0].name,
        };
      }

      // Get the selected environment details
      const environment = environments.find(e => e.id === selectedEnvironment);
      if (!environment) {
        return {
          exists: true,
          hasEnvironment: true,
          environmentMatches: false,
          issues: ['Selected environment not found in environment list'],
        };
      }

      // Use the helper function to validate for the selected environment
      return validateCfgJsonForEnvironment(cfgData, environment);
    } catch (err: any) {
      console.error(`[cfg.json] Failed to load or validate for ${cacheKey}:`, err);

      if (err.response?.status === 404) {
        return {
          exists: false,
          hasEnvironment: false,
          environmentMatches: false,
          issues: ['cfg.json not found for this activity'],
        };
      }

      return {
        exists: false,
        hasEnvironment: false,
        environmentMatches: false,
        issues: [`Failed to load cfg.json: ${err.message}`],
      };
    }
  };

  // Validate cfg.json for all blocks in an activity, or activity-level if no blocks
  const validateCfgJsonForAllBlocks = async (activity: CMI5Activity, config: { aus: RC5AU[]; blocks?: RC5Block[] }) => {
    if (config.blocks && config.blocks.length > 0) {
      // Multi-block: validate per-block
      const validationPromises = config.blocks.map(async (block) => {
        const validation = await validateCfgJson(activity, block.blockName);
        const cacheKey = getCfgCacheKey(activity, block.blockName);
        return { cacheKey, validation };
      });
      const results = await Promise.all(validationPromises);
      setCfgValidations(prev => {
        const newMap = new Map(prev);
        results.forEach(({ cacheKey, validation }) => {
          newMap.set(cacheKey, validation);
        });
        return newMap;
      });
    } else {
      // No blocks: validate at activity level
      const activityKey = getActivityKey(activity);
      const validation = await validateCfgJson(activity);
      setCfgValidations(prev => new Map(prev).set(activityKey, validation));
    }
  };

  const loadMappingMatrix = async (activity: CMI5Activity, configToUse?: { aus: RC5AU[]; blocks?: RC5Block[] }) => {
    const activityKey = getActivityKey(activity);
    const config = configToUse || activityConfigs.get(activityKey);

    console.log('[loadMappingMatrix] Starting for activity:', activityKey);
    console.log('[loadMappingMatrix] Config exists:', !!config);
    console.log('[loadMappingMatrix] Config provided as parameter:', !!configToUse);
    console.log('[loadMappingMatrix] Environments count:', environments.length);

    if (!config || !config.aus || config.aus.length === 0) {
      console.log('[loadMappingMatrix] Early return - no config or AUs');
      return;
    }

    if (environments.length === 0) {
      console.log('[loadMappingMatrix] Early return - no environments');
      return;
    }

    // Filter to AUs with scenarios (from RC5.yaml) OR with existing RangeOS API mappings
    const ausWithScenarios = config.aus.filter(au =>
      au.rangeosScenarioName || au.rangeosScenarioUUID ||
      (au.mappedScenarios && au.mappedScenarios.length > 0)
    );
    if (ausWithScenarios.length === 0) {
      console.log('[loadMappingMatrix] No AUs with scenarios found');
      return;
    }

    const auIds = ausWithScenarios.map(au => au.id);
    console.log('[loadMappingMatrix] Will query for', auIds.length, 'AU(s) with scenarios across', environments.length, 'environment(s)');

    try {
      // Query all environments for AU mappings AND scenario availability
      const envDataPromises = environments.map(async (env) => {
        try {
          // Fetch AU mappings
          const mappings = await rangeosEnvironmentService.getAUMappings(env.id, auIds);
          const mappingMap = new Map<string, boolean>();
          const mappedAuIds = new Set(mappings.map(m => m.auId));

          // Check scenario availability for each AU
          const availabilityMap = new Map<string, boolean>();
          const scenarioSearchPromises = ausWithScenarios.map(async (au) => {
            if (!au.rangeosScenarioName) {
              // AU has a RangeOS API mapping but no RC5.yaml scenario name to search by —
              // treat it as available if it's mapped in this environment
              availabilityMap.set(au.id, mappedAuIds.has(au.id));
              return;
            }

            try {
              // Search for the scenario in this environment
              const scenarios = await rangeosEnvironmentService.searchScenarios(env.id, au.rangeosScenarioName);
              const scenarioExists = scenarios.length > 0;
              availabilityMap.set(au.id, scenarioExists);
              console.log(`[loadMappingMatrix] AU "${au.title}" scenario "${au.rangeosScenarioName}" in ${env.name}: ${scenarioExists ? 'FOUND' : 'NOT FOUND'}`);
            } catch (err) {
              console.warn(`Failed to search scenarios for AU ${au.id} in environment ${env.id}:`, err);
              availabilityMap.set(au.id, false);
            }
          });

          await Promise.all(scenarioSearchPromises);

          // Mark which AUs have mappings
          auIds.forEach(auId => {
            mappingMap.set(auId, mappedAuIds.has(auId));
          });

          return { envId: env.id, mappingMap, availabilityMap };
        } catch (err) {
          console.error(`Failed to load data for environment ${env.id}:`, err);
          // Return empty maps on error
          const mappingMap = new Map<string, boolean>();
          const availabilityMap = new Map<string, boolean>();
          auIds.forEach(auId => {
            mappingMap.set(auId, false);
            availabilityMap.set(auId, false);
          });
          return { envId: env.id, mappingMap, availabilityMap };
        }
      });

      const envData = await Promise.all(envDataPromises);

      // Build the matrices: environmentId -> auId -> boolean
      const activityMatrix = new Map<string, Map<string, boolean>>();
      const activityAvailability = new Map<string, Map<string, boolean>>();

      envData.forEach(({ envId, mappingMap, availabilityMap }) => {
        activityMatrix.set(envId, mappingMap);
        activityAvailability.set(envId, availabilityMap);
      });

      // Update both matrices for this activity
      console.log('[loadMappingMatrix] Setting matrices with', activityMatrix.size, 'environment(s)');
      setMappingMatrix(prev => {
        const newMatrix = new Map(prev);
        newMatrix.set(activityKey, activityMatrix);
        console.log('[loadMappingMatrix] Mapping matrix updated. Total activities in matrix:', newMatrix.size);
        return newMatrix;
      });

      setScenarioAvailability(prev => {
        const newMatrix = new Map(prev);
        newMatrix.set(activityKey, activityAvailability);
        console.log('[loadMappingMatrix] Scenario availability matrix updated.');
        return newMatrix;
      });
    } catch (err: any) {
      console.error('[loadMappingMatrix] Failed to load mapping matrix:', err);
    }
  };

  /**
   * Validate all activities comprehensively
   */
  const handleValidateActivities = async () => {
    if (activities.length === 0) {
      setError('No activities to validate');
      return;
    }

    setValidating(true);
    setError(null);
    setSuccessMessage(null);

    try {
      console.log('[Validation] Starting validation for', activities.length, 'activities');
      console.log('[Validation] Source ID:', sourceId);
      console.log('[Validation] Selected environment:', selectedEnvironment);

      const validations = await cmi5ConfigService.validateActivities(
        sourceId,
        activities.map(a => ({
          activityId: a.activityId,
          activityName: a.activityName,
          tenantId: a.tenantId,
          courseId: a.courseId,
          moodleCourseId: a.moodleCourseId || 0,
          cmi5activityid: a.cmi5activityid,
        })),
        selectedEnvironment || undefined
      );

      console.log('[Validation] Received validations:', validations);

      // Store validation results by activity key
      const resultsMap = new Map<string, any>();
      validations.forEach((validation: any) => {
        const key = `${validation.tenantId}-${validation.courseId}`;
        resultsMap.set(key, validation);
      });

      setValidationResults(resultsMap);
      setShowValidation(true);

      const totalValid = validations.filter((v: any) => v.overallValid).length;
      const totalInvalid = validations.length - totalValid;

      if (totalInvalid === 0) {
        setSuccessMessage(`All ${validations.length} activities are fully configured and ready!`);
      } else {
        setError(`${totalInvalid} of ${validations.length} activities have configuration issues`);
      }
    } catch (err: any) {
      console.error('[Validation] Failed:', err);
      setError(`Validation failed: ${err.message || 'Unknown error'}`);
    } finally {
      setValidating(false);
    }
  };

  const handleEditIndividualAU = async (activity: CMI5Activity, auId: string) => {
    // If no environment selected, prompt user to select one
    if (!selectedEnvironment) {
      setEditingAU(auId);
      setError('Please select a RangeOS environment to configure this AU');
      return;
    }

    // Open dialog with just this AU
    setScenarioDialogActivity(activity);
    setEditingAU(auId);
    setScenarioDialogOpen(true);
  };

  const handleScenarioMappingSave = async (
    mappings: Array<{
      auId: string;
      scenarioId: string;
      scenarioName: string;
      mappingType?: 'draft' | 'existing';
      draftScenarioUuid?: string;
      draftScenarioVersion?: string;
    }>,
    rangeosEnvironmentId: string
  ) => {
    if (!scenarioDialogActivity) return;

    setLoading(true);
    setError(null);

    try {
      // Save each AU mapping to the RangeOS environment
      const savePromises = mappings.map(async (mapping) => {
        try {
          // If this is a draft mapping, import the draft to RangeOS first
          let scenarioIdToUse = mapping.scenarioId;
          if (mapping.mappingType === 'draft' && mapping.draftScenarioUuid) {
            if (!s3BucketId || !s3EnvironmentId) {
              throw new Error('S3 bucket and environment are required for draft import');
            }

            console.log(`[CMI5Browser] Importing draft scenario ${mapping.draftScenarioUuid} to RangeOS before creating AU mapping`);

            const activityId = parseInt(scenarioDialogActivity.courseId, 10) || undefined;

            const importResult = await rangeosEnvironmentService.importDraftFromS3(rangeosEnvironmentId, {
              s3BucketId,
              s3EnvironmentId,
              scenarioUuid: mapping.draftScenarioUuid,
              activityId,
            });

            if (!importResult.success) {
              throw new Error(importResult.message || 'Failed to import draft scenario');
            }

            console.log(`[CMI5Browser] Successfully imported draft. New scenario ID: ${importResult.scenarioId}`);
            scenarioIdToUse = importResult.scenarioId;
          }

          await rangeosEnvironmentService.saveAUMapping(rangeosEnvironmentId, {
            auId: mapping.auId,
            name: mapping.scenarioName || `Mapping for ${mapping.auId}`,
            scenarios: [scenarioIdToUse],
          });
        } catch (err: any) {
          console.error(`Failed to save AU mapping for ${mapping.auId}:`, err);
          throw new Error(`Failed to save mapping for ${mapping.auId}: ${err.message}`);
        }
      });

      await Promise.all(savePromises);

      // Reload the activity config to show updated mappings
      const activityKey = `${scenarioDialogActivity.tenantId}-${scenarioDialogActivity.courseId}`;
      setActivityConfigs(prev => {
        const newMap = new Map(prev);
        newMap.delete(activityKey);
        return newMap;
      });
      await loadActivityConfig(scenarioDialogActivity);

      // Reload mapping matrix to show updated status
      await loadMappingMatrix(scenarioDialogActivity);

      // Show success message
      setSuccessMessage(`Successfully saved ${mappings.length} AU mapping(s)`);
      setTimeout(() => setSuccessMessage(null), 5000);

      // Close dialogs
      setScenarioDialogOpen(false);
      setScenarioDialogActivity(null);
      setEditingAU(null);
      // Keep the environment selected so the user can see the new mappings!
    } catch (err: any) {
      setError(err.message || 'Failed to save activity configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCfgEditor = async (activity: CMI5Activity, environment?: RangeOSEnvironment, blockName?: string) => {
    try {
      setLoading(true);
      setCfgEditorError(null);
      setCfgEditorShowDiff(false);
      setCfgEditorExpectedData('');
      setCfgEditorBlock(blockName || null);

      const block = blockName || getFirstBlockName(activity);
      const cfgData = await cmi5ConfigService.getCfgJson(
        sourceId,
        activity.tenantId,
        activity.courseId,
        block
      );

      if (environment) {
        // Environment-specific mode: edit only this environment's location stanza
        // Match by playerUrl (the 'url' field in cfg.json)
        const location = cfgData.locations?.find(
          (loc: any) => loc.url === environment.playerUrl
        );

        if (location) {
          // Edit existing location
          setCfgEditorData(JSON.stringify(location, null, 2));

          // Check if there are mismatches to enable diff view
          const validation = validateCfgJsonForEnvironment(cfgData, environment);
          if (validation.issues && validation.issues.length > 0) {
            // Create expected location data for diff
            const expectedLocation = {
              name: environment.name,
              url: environment.playerUrl,
              config: {
                DEVOPS_API_URL: environment.devOpsApiUrl,
                DEVOPS_GQL_URL: environment.graphqlUrl || '',
                DEVOPS_GQL_SUBSCRIPTIONS_URL: environment.graphqlSubscriptionsUrl || '',
              },
            };
            setCfgEditorExpectedData(JSON.stringify(expectedLocation, null, 2));
            setCfgEditorShowDiff(true);
          }
        } else {
          // Create new location for this environment
          const newLocation = {
            name: environment.name,
            url: environment.playerUrl || environment.devOpsApiUrl,
            config: {
              DEVOPS_API_URL: environment.devOpsApiUrl,
              DEVOPS_GQL_URL: environment.graphqlUrl || '',
              DEVOPS_GQL_SUBSCRIPTIONS_URL: environment.graphqlSubscriptionsUrl || '',
            },
          };
          setCfgEditorData(JSON.stringify(newLocation, null, 2));
        }
        setCfgEditorEnvironment(environment);
      } else {
        // Full mode: edit entire cfg.json
        setCfgEditorData(JSON.stringify(cfgData, null, 2));
        setCfgEditorEnvironment(null);
      }

      setCfgEditorActivity(activity);
      setCfgEditorOpen(true);
    } catch (err: any) {
      setCfgEditorError(err.message || 'Failed to load cfg.json');
      setError(err.message || 'Failed to load cfg.json');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCfgJson = async () => {
    if (!cfgEditorActivity) return;

    try {
      setLoading(true);
      setCfgEditorError(null);

      // Parse the JSON to validate it
      const parsedData = JSON.parse(cfgEditorData);

      const block = cfgEditorBlock || getFirstBlockName(cfgEditorActivity);

      if (cfgEditorEnvironment) {
        // Environment-specific mode: merge this location back into the full cfg.json
        let fullCfgData: CfgJson;

        if (sourceType === 's3') {
          const packageKey = cfgEditorActivity.courseId;
          if (!s3BucketId || !s3EnvironmentId) {
            throw new Error('S3 bucket and environment IDs are required for S3 sources');
          }
          fullCfgData = await s3BucketsApi.getCfgJson(s3BucketId, s3EnvironmentId, packageKey);
        } else {
          fullCfgData = await cmi5ConfigService.getCfgJson(
            sourceId,
            cfgEditorActivity.tenantId,
            cfgEditorActivity.courseId,
            block
          );
        }

        if (!fullCfgData.locations) {
          fullCfgData.locations = [];
        }

        // Find existing location for this environment by matching playerUrl
        const locationIndex = fullCfgData.locations.findIndex(
          (loc: any) => loc.url === cfgEditorEnvironment.playerUrl
        );

        if (locationIndex >= 0) {
          // Update existing location
          fullCfgData.locations[locationIndex] = parsedData;
        } else {
          // Add new location
          fullCfgData.locations.push(parsedData);
        }

        // Save the full cfg.json
        if (sourceType === 's3') {
          const packageKey = cfgEditorActivity.courseId;
          if (!s3BucketId || !s3EnvironmentId) {
            throw new Error('S3 bucket and environment IDs are required for S3 sources');
          }
          await s3BucketsApi.updateCfgJson(s3BucketId, s3EnvironmentId, packageKey, fullCfgData);
        } else {
          await cmi5ConfigService.updateCfgJson(
            sourceId,
            cfgEditorActivity.tenantId,
            cfgEditorActivity.courseId,
            fullCfgData,
            block
          );
        }
      } else {
        // Full mode: save entire cfg.json
        // Ensure parsedData has the correct format: { "locations": [...] }
        let fullCfgData;
        if (parsedData.locations && Array.isArray(parsedData.locations)) {
          // parsedData already has the correct format with locations array
          fullCfgData = { locations: parsedData.locations };
        } else {
          // parsedData is missing the locations array wrapper, add it
          fullCfgData = {
            locations: Array.isArray(parsedData) ? parsedData : [parsedData]
          };
        }

        if (sourceType === 's3') {
          const packageKey = cfgEditorActivity.courseId;
          if (!s3BucketId || !s3EnvironmentId) {
            throw new Error('S3 bucket and environment IDs are required for S3 sources');
          }
          await s3BucketsApi.updateCfgJson(s3BucketId, s3EnvironmentId, packageKey, fullCfgData);
        } else {
          await cmi5ConfigService.updateCfgJson(
            sourceId,
            cfgEditorActivity.tenantId,
            cfgEditorActivity.courseId,
            fullCfgData,
            block
          );
        }
      }

      // Re-validate the cfg.json and update cache
      const validation = await validateCfgJson(cfgEditorActivity, cfgEditorBlock || undefined);
      const cacheKey = getCfgCacheKey(cfgEditorActivity, cfgEditorBlock || undefined);
      setCfgValidations(prev => new Map(prev).set(cacheKey, validation));

      setSuccessMessage(
        cfgEditorEnvironment
          ? `cfg.json updated for ${cfgEditorEnvironment.name}`
          : 'cfg.json updated successfully'
      );
      setTimeout(() => setSuccessMessage(null), 5000);
      setCfgEditorOpen(false);
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        setCfgEditorError(`Invalid JSON: ${err.message}`);
      } else {
        setCfgEditorError(err.message || 'Failed to save cfg.json');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUploadPackage = async (activity: CMI5Activity) => {
    if (sourceType !== 's3') return;

    const packageKey = activity.courseId;
    const activityKey = getActivityKey(activity);

    if (!s3BucketId || !s3EnvironmentId) {
      setError('S3 bucket and environment IDs are required for S3 sources');
      return;
    }

    try {
      setUploadingPackages(prev => new Set(prev).add(activityKey));
      setError(null);

      // Re-package and upload to S3
      const result = await s3BucketsApi.uploadPackage(s3BucketId, s3EnvironmentId, packageKey);

      if (result.success) {
        setSuccessMessage(`Package "${activity.activityName}" uploaded successfully to S3`);
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err: any) {
      setError(err.message || `Failed to upload package: ${activity.activityName}`);
    } finally {
      setUploadingPackages(prev => {
        const newSet = new Set(prev);
        newSet.delete(activityKey);
        return newSet;
      });
    }
  };

  const handleExportToS3 = (activity: CMI5Activity) => {
    setS3ExportActivity(activity);
    setS3ExportDialogOpen(true);
  };

  const handleToggleSelectActivity = (activity: CMI5Activity) => {
    const activityKey = getActivityKey(activity);
    setSelectedActivities(prev => {
      const newSet = new Set(prev);
      if (newSet.has(activityKey)) {
        newSet.delete(activityKey);
      } else {
        newSet.add(activityKey);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedActivities.size === activities.length) {
      setSelectedActivities(new Set());
    } else {
      setSelectedActivities(new Set(activities.map(getActivityKey)));
    }
  };

  const handleBatchExport = () => {
    setBatchExportDialogOpen(true);
  };

  const getSelectedActivitiesArray = (): CMI5Activity[] => {
    return activities.filter(activity => selectedActivities.has(getActivityKey(activity)));
  };

  const handleEditAULinkage = (activity: CMI5Activity, au: RC5AU) => {
    setLinkageEditActivity(activity);
    setLinkageEditAU(au);
    setLinkageEditDialogOpen(true);
  };

  /**
   * Export scenario definition from a RangeOS environment to S3 package
   */
  const handleExportScenario = async (activity: CMI5Activity, au: RC5AU, fromEnvironmentId: string) => {
    if (sourceType !== 's3') {
      setError('Scenario export is only available for S3 sources');
      return;
    }

    if (!au.rangeosScenarioUUID) {
      setError('No scenario UUID found for this AU');
      return;
    }

    if (!s3BucketId || !s3EnvironmentId) {
      setError('S3 bucket and environment IDs are required');
      return;
    }

    const packageKey = activity.courseId;

    try {
      setExportingScenarios(prev => new Set(prev).add(`${au.id}-${fromEnvironmentId}`));
      setError(null);

      // Get the RangeOS environment
      const rangeosEnv = await rangeosEnvironmentService.getEnvironment(fromEnvironmentId);

      // Export scenario from RangeOS API and save to S3
      // The server will auto-create an API endpoint if needed
      // Note: bearerToken is no longer needed - server uses user's Keycloak token
      await s3BucketsApi.exportScenarioFromRangeOS(
        s3BucketId,
        s3EnvironmentId,
        packageKey,
        rangeosEnv.devOpsApiUrl,
        au.rangeosScenarioUUID,
        au.id
      );

      // Track saved scenarios by scenario UUID, not AU ID
      // This way, multiple AUs referencing the same scenario will all show as saved
      if (au.rangeosScenarioUUID) {
        setSavedScenarioDefinitions(prev => new Set(prev).add(au.rangeosScenarioUUID));
      }
      setSuccessMessage(`Scenario "${au.rangeosScenarioName}" exported from ${rangeosEnv.name} and saved to S3 package`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      // Check if this is a "no draft" error
      if (err.response?.status === 404 && err.response?.data?.error === 'NO_DRAFT') {
        setError(`Scenario "${au.rangeosScenarioName}" exists but has no design draft. It may have been created directly rather than through the scenario designer, so it cannot be exported.`);
      } else {
        setError(err.message || `Failed to export scenario: ${au.rangeosScenarioName}`);
      }
    } finally {
      setExportingScenarios(prev => {
        const newSet = new Set(prev);
        newSet.delete(`${au.id}-${fromEnvironmentId}`);
        return newSet;
      });
    }
  };

  /**
   * Import scenario definition from S3 package to a target RangeOS environment
   */
  const handleImportScenario = async (activity: CMI5Activity, au: RC5AU, targetEnvironmentId: string) => {
    if (sourceType !== 's3') {
      setError('Scenario import is only available for S3 sources');
      return;
    }

    if (!s3BucketId || !s3EnvironmentId) {
      setError('S3 bucket and environment IDs are required');
      return;
    }

    const packageKey = activity.courseId;

    try {
      setImportingScenarios(prev => new Set(prev).add(`${au.id}-${targetEnvironmentId}`));
      setError(null);

      // Get the target RangeOS environment
      const rangeosEnv = await rangeosEnvironmentService.getEnvironment(targetEnvironmentId);

      // Import scenario from S3 to RangeOS API
      // The server will auto-create an API endpoint if needed
      // Note: bearerToken is no longer needed - server uses user's Keycloak token
      // Pass both auId (target AU) and scenarioUuid (which scenario to load)
      const result = await s3BucketsApi.importScenarioToRangeOS(
        s3BucketId,
        s3EnvironmentId,
        packageKey,
        rangeosEnv.devOpsApiUrl,
        au.id,
        au.rangeosScenarioUUID  // Pass the scenario UUID to load the correct definition
      );

      if (result.success) {
        setSuccessMessage(`Scenario imported to ${rangeosEnv.name}. New draft ID: ${result.draftId}`);
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err: any) {
      setError(err.message || `Failed to import scenario for AU: ${au.title}`);
    } finally {
      setImportingScenarios(prev => {
        const newSet = new Set(prev);
        newSet.delete(`${au.id}-${targetEnvironmentId}`);
        return newSet;
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            CMI5 Activities - {sourceName}
          </Typography>
          <IconButton
            onClick={() => {
              setActivityConfigs(new Map());
              setError(null);
            }}
            disabled={loading}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : activities.length === 0 ? (
          <Alert severity="info">
            No CMI5 activities found in this Moodle instance.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Validation toolbar */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
              <Button
                variant="outlined"
                startIcon={validating ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                onClick={handleValidateActivities}
                disabled={validating || activities.length === 0}
              >
                {validating ? 'Validating...' : 'Validate All Activities'}
              </Button>
              {showValidation && validationResults.size > 0 && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip
                    size="small"
                    label={`${Array.from(validationResults.values()).filter((v: any) => v.overallValid).length} Valid`}
                    color="success"
                    icon={<CheckCircleIcon />}
                  />
                  <Chip
                    size="small"
                    label={`${Array.from(validationResults.values()).filter((v: any) => !v.overallValid).length} Issues`}
                    color="error"
                    icon={<WarningIcon />}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setShowValidation(false)}
                    title="Hide validation results"
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>

            {/* Multi-select toolbar - only show for Moodle sources */}
            {sourceType === 'moodle' && (
              <Toolbar
                sx={{
                  pl: { sm: 2 },
                  pr: { xs: 1, sm: 1 },
                  bgcolor: selectedActivities.size > 0 ? 'action.selected' : 'transparent',
                  borderRadius: 1,
                }}
              >
                <Checkbox
                  indeterminate={selectedActivities.size > 0 && selectedActivities.size < activities.length}
                  checked={activities.length > 0 && selectedActivities.size === activities.length}
                  onChange={handleSelectAll}
                />
                {selectedActivities.size > 0 ? (
                  <>
                    <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1">
                      {selectedActivities.size} selected
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={handleBatchExport}
                    >
                      Export to S3
                    </Button>
                  </>
                ) : (
                  <Typography sx={{ flex: '1 1 100%' }} variant="body2" color="text.secondary">
                    Select activities to batch export
                  </Typography>
                )}
              </Toolbar>
            )}

            {Array.from(groupedActivities.entries()).map(([moodleCourseId, courseActivities]) => (
              <Box key={moodleCourseId} sx={{ mb: 2 }}>
                {/* Course group header */}
                {groupedActivities.size > 1 && (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    mb: 0.5,
                  }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {courseNames.get(moodleCourseId) || (moodleCourseId ? `Course ${moodleCourseId}` : 'Unknown Course')}
                    </Typography>
                    {moodleCourseId > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        (ID: {moodleCourseId})
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      — {courseActivities.length} activit{courseActivities.length !== 1 ? 'ies' : 'y'}
                    </Typography>
                  </Box>
                )}
              {courseActivities.map((activity) => {
              const activityKey = getActivityKey(activity);
              const config = activityConfigs.get(activityKey);
              const ausWithScenarios = config?.aus?.filter(
                au => au.rangeosScenarioName || au.rangeosScenarioUUID ||
                      (au.mappedScenarios && au.mappedScenarios.length > 0)
              ) || [];

              // Check for warnings - cfg.json issues or unmapped AUs for selected environment
              const hasWarnings = (() => {
                if (!selectedEnvironment || !config) return false;

                // Check cfg.json validation
                const cfgValidation = cfgValidations.get(activityKey);
                if (cfgValidation && (!cfgValidation.exists || !cfgValidation.hasEnvironment || !cfgValidation.environmentMatches)) {
                  return true;
                }

                // Check if any AUs with scenarios are unmapped in the selected environment
                const activityMatrix = mappingMatrix.get(activityKey);
                const envMappings = activityMatrix?.get(selectedEnvironment);
                if (envMappings && ausWithScenarios.length > 0) {
                  const unmappedCount = ausWithScenarios.filter(au => !envMappings.get(au.id)).length;
                  if (unmappedCount > 0) {
                    return true;
                  }
                }

                return false;
              })();

              return (
                <Accordion
                  key={activityKey}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      {/* Checkbox for multi-select - only show for Moodle sources */}
                      {sourceType === 'moodle' && (
                        <Checkbox
                          checked={selectedActivities.has(activityKey)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleSelectActivity(activity);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>
                            {activity.activityName}
                          </Typography>
                          {activity.versionInfo && (
                            <Chip
                              label={`${activity.versionInfo.currentVersion} (${activity.versionInfo.totalVersions})`}
                              size="small"
                              color={activity.versionInfo.hasVersionHistory ? 'primary' : 'default'}
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                        {activity.size !== undefined && activity.lastModified && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {(activity.size / (1024 * 1024)).toFixed(2)} MB • {new Date(activity.lastModified).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                      {/* Validation status */}
                      {showValidation && validationResults.has(activityKey) && (() => {
                        const validation = validationResults.get(activityKey);
                        if (validation.overallValid) {
                          return (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Valid"
                              size="small"
                              color="success"
                            />
                          );
                        } else {
                          return (
                            <Chip
                              icon={<WarningIcon />}
                              label={`${validation.summary.ausWithIssues} issue${validation.summary.ausWithIssues !== 1 ? 's' : ''}`}
                              size="small"
                              color="error"
                            />
                          );
                        }
                      })()}
                      {hasWarnings && (
                        <Chip
                          icon={<WarningIcon />}
                          label="Issues"
                          size="small"
                          color="warning"
                        />
                      )}
                      {config ? (
                        <>
                          <Chip
                            label={`${config.aus?.length || 0} AUs`}
                            size="small"
                            color="primary"
                          />
                          {ausWithScenarios.length > 0 && (
                            <Chip
                              label={`${ausWithScenarios.length} with scenarios`}
                              size="small"
                              color="success"
                            />
                          )}
                        </>
                      ) : loadingConfigs.has(activityKey) ? (
                        <CircularProgress size={16} />
                      ) : null}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    {loadingConfigs.has(activityKey) ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Tenant ID:</strong> {activity.tenantId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Course ID:</strong> {activity.courseId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Activity ID:</strong> {activity.activityId}
                          </Typography>
                          {activity.size !== undefined && (
                            <Typography variant="caption" color="text.secondary">
                              <strong>Size:</strong> {(activity.size / (1024 * 1024)).toFixed(2)} MB
                            </Typography>
                          )}
                          {activity.lastModified && (
                            <Typography variant="caption" color="text.secondary">
                              <strong>Last Modified:</strong> {new Date(activity.lastModified).toLocaleString()}
                            </Typography>
                          )}
                        </Box>

                        {/* Comprehensive Validation Results */}
                        {showValidation && validationResults.has(activityKey) && (() => {
                          const validation = validationResults.get(activityKey);
                          return (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Validation Results
                              </Typography>

                              {/* Overall Status */}
                              <Box sx={{ mb: 2 }}>
                                <Alert severity={validation.overallValid ? 'success' : 'error'} sx={{ fontSize: '0.875rem' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {validation.overallValid
                                      ? `✓ All ${validation.summary.totalAUs} AUs are properly configured`
                                      : `✗ ${validation.summary.ausWithIssues} of ${validation.summary.totalAUs} AUs have issues`
                                    }
                                  </Typography>
                                </Alert>
                              </Box>

                              {/* Per-AU Validation Details */}
                              {validation.checks.ausValid?.auValidations && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>AU Details:</Typography>
                                  {validation.checks.ausValid.auValidations.map((auValidation: any) => (
                                    <Box
                                      key={auValidation.auId}
                                      sx={{
                                        p: 1.5,
                                        mb: 1,
                                        border: 1,
                                        borderColor: auValidation.overallValid ? 'success.main' : 'error.main',
                                        borderRadius: 1,
                                        bgcolor: auValidation.overallValid ? 'success.lighter' : 'error.lighter',
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        {auValidation.overallValid ? (
                                          <CheckCircleIcon color="success" fontSize="small" />
                                        ) : (
                                          <WarningIcon color="error" fontSize="small" />
                                        )}
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                          {auValidation.auName || auValidation.auId}
                                        </Typography>
                                      </Box>

                                      <Box sx={{ pl: 3 }}>
                                        {/* Exists in Player */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {auValidation.checks.existsInPlayer.valid ? (
                                            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                          ) : (
                                            <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                          )}
                                          <Typography variant="caption">
                                            {auValidation.checks.existsInPlayer.message ||
                                              (auValidation.checks.existsInPlayer.valid ? 'Content exists in player' : 'Content missing from player')}
                                          </Typography>
                                        </Box>

                                        {/* cfg.json Entry */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {auValidation.checks.hasCfgEntry.valid ? (
                                            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                          ) : (
                                            <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                          )}
                                          <Typography variant="caption">
                                            {auValidation.checks.hasCfgEntry.message ||
                                              (auValidation.checks.hasCfgEntry.valid ? 'cfg.json configured' : 'cfg.json not configured')}
                                          </Typography>
                                        </Box>

                                        {/* Scenario Status */}
                                        {auValidation.checks.scenarioValid && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {auValidation.checks.scenarioValid.valid ? (
                                              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                            ) : (
                                              <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                            )}
                                            <Typography variant="caption">
                                              {auValidation.checks.scenarioValid.message ||
                                                (auValidation.checks.scenarioValid.valid ? 'Scenario found' : 'Scenario not found')}
                                            </Typography>
                                          </Box>
                                        )}

                                        {/* AU Mapping Status */}
                                        {auValidation.checks.auMappingValid && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {auValidation.checks.auMappingValid.valid ? (
                                              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                            ) : (
                                              <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                            )}
                                            <Typography variant="caption">
                                              {auValidation.checks.auMappingValid.message ||
                                                (auValidation.checks.auMappingValid.valid ? 'AU mapping exists' : 'AU mapping missing')}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          );
                        })()}

                        {/* cfg.json Validation Status - only show at activity level for activities WITHOUT blocks */}
                        {(!config?.blocks || config.blocks.length === 0) && (() => {
                          const validation = cfgValidations.get(activityKey);
                          if (!validation) return null;

                          return (
                            <Box sx={{ mt: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="subtitle2">cfg.json Configuration</Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {sourceType === 'moodle' && (
                                    <Button
                                      size="small"
                                      startIcon={<UploadIcon />}
                                      onClick={() => handleExportToS3(activity)}
                                      variant="outlined"
                                      color="primary"
                                    >
                                      Export to S3
                                    </Button>
                                  )}
                                  <Button
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => handleOpenCfgEditor(activity)}
                                    variant="outlined"
                                  >
                                    Edit cfg.json
                                  </Button>
                                </Box>
                              </Box>
                              {!validation.exists ? (
                                <Alert severity="error" sx={{ fontSize: '0.875rem' }}>
                                  <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                    cfg.json Not Found
                                  </Typography>
                                  {validation.issues && validation.issues.map((issue, idx) => (
                                    <Typography key={idx} variant="caption" display="block">
                                      • {issue}
                                    </Typography>
                                  ))}
                                </Alert>
                              ) : !validation.hasEnvironment && selectedEnvironment ? (
                                <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
                                  <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                    Environment Not Configured in cfg.json
                                  </Typography>
                                  {validation.issues && validation.issues.map((issue, idx) => (
                                    <Typography key={idx} variant="caption" display="block">
                                      • {issue}
                                    </Typography>
                                  ))}
                                </Alert>
                              ) : validation.environmentMatches ? (
                                <Alert severity="success" sx={{ fontSize: '0.875rem' }}>
                                  <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                    cfg.json Valid - Environment Configured
                                  </Typography>
                                  {validation.configuredLocationName && (
                                    <Typography variant="caption" display="block">
                                      • Location: {validation.configuredLocationName}
                                    </Typography>
                                  )}
                                </Alert>
                              ) : validation.hasEnvironment && validation.issues && validation.issues.length > 0 ? (
                                <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
                                  <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                    cfg.json Configuration Mismatch
                                  </Typography>
                                  {validation.issues.map((issue, idx) => (
                                    <Typography key={idx} variant="caption" display="block">
                                      • {issue}
                                    </Typography>
                                  ))}
                                </Alert>
                              ) : validation.exists && !selectedEnvironment ? (
                                <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                                  <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                    cfg.json Found
                                  </Typography>
                                  {validation.configuredLocationName && (
                                    <Typography variant="caption" display="block">
                                      • Has location: {validation.configuredLocationName}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" display="block">
                                    • Select an environment to validate configuration
                                  </Typography>
                                </Alert>
                              ) : null}
                            </Box>
                          );
                        })()}

                        {/* Mapping Matrix View - Only show for activities with labs (AUs) */}
                        {(() => {
                          const hasLabs = config && config.aus && config.aus.length > 0;
                          const shouldShow = hasLabs && environments.length > 0 && mappingMatrix.has(activityKey);
                          console.log('[Matrix Debug]', {
                            activityKey,
                            hasConfig: !!config,
                            hasLabs,
                            ausCount: config?.aus?.length || 0,
                            environmentsCount: environments.length,
                            hasMatrixData: mappingMatrix.has(activityKey),
                            shouldShow,
                          });
                          return shouldShow;
                        })() && (
                          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                            <Typography variant="subtitle2" sx={{ mb: 2 }}>
                              AU Mapping Status Across Environments
                            </Typography>
                            <Box sx={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                                <thead>
                                  <tr>
                                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: `2px solid ${theme.palette.divider}`, fontWeight: 600 }}>
                                      AU
                                    </th>
                                    {(() => {
                                      console.log('[Matrix Render] Rendering headers for', environments.length, 'environments');
                                      console.log('[Matrix Render] Environment names:', environments.map(e => e.name));
                                      console.log('[Matrix Render] Selected environment:', selectedEnvironment);
                                      console.log('[Matrix Render] Selected environment type:', typeof selectedEnvironment);
                                      return environments.map((env) => {
                                        const isSelected = env.id === selectedEnvironment;
                                        console.log(`[Matrix Render] Env ${env.name} (${env.id}): isSelected=${isSelected} (comparing "${env.id}" === "${selectedEnvironment}")`);
                                        return (
                                          <th
                                            key={env.id}
                                            style={{
                                              textAlign: 'center',
                                              padding: '8px',
                                              borderBottom: `2px solid ${theme.palette.divider}`,
                                              fontWeight: 600,
                                              backgroundColor: isSelected ? theme.palette.action.selected : undefined,
                                              position: 'relative'
                                            }}
                                          >
                                            {env.name}
                                            {isSelected && (
                                              <span style={{
                                                fontSize: '0.7rem',
                                                color: theme.palette.primary.main,
                                                display: 'block',
                                                fontWeight: 500
                                              }}>
                                                (Selected)
                                              </span>
                                            )}
                                          </th>
                                        );
                                      });
                                    })()}
                                  </tr>
                                </thead>
                                <tbody>
                                  {/* cfg.json Status Row - only at activity level for activities WITHOUT blocks */}
                                  {(!config?.blocks || config.blocks.length === 0) && <tr style={{ backgroundColor: theme.palette.action.hover }}>
                                    <td style={{ padding: '12px', borderBottom: `2px solid ${theme.palette.divider}`, fontWeight: 600 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        cfg.json Config
                                      </Typography>
                                    </td>
                                    {environments.map((env) => {
                                      const cfgData = cfgDataCache.get(activityKey);
                                      const isSelected = env.id === selectedEnvironment;

                                      if (!cfgData) {
                                        return (
                                          <td
                                            key={env.id}
                                            style={{
                                              textAlign: 'center',
                                              padding: '12px',
                                              borderBottom: `2px solid ${theme.palette.divider}`,
                                              backgroundColor: isSelected ? theme.palette.action.selected : undefined
                                            }}
                                          >
                                            <Typography variant="caption" color="text.secondary">
                                              No cfg.json
                                            </Typography>
                                          </td>
                                        );
                                      }

                                      // Validate this environment against cfg.json
                                      const validation = validateCfgJsonForEnvironment(cfgData, env);
                                      const isConfigured = validation.hasEnvironment && validation.environmentMatches;
                                      const hasIssues = validation.issues && validation.issues.length > 0;
                                      const isMissing = !validation.hasEnvironment;

                                      return (
                                        <td
                                          key={env.id}
                                          style={{
                                            textAlign: 'center',
                                            padding: '12px',
                                            borderBottom: `2px solid ${theme.palette.divider}`,
                                            backgroundColor: isSelected ? theme.palette.action.selected : undefined
                                          }}
                                        >
                                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                            {isConfigured && !hasIssues ? (
                                              <>
                                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
                                                <Button
                                                  size="small"
                                                  variant="text"
                                                  onClick={() => handleOpenCfgEditor(activity, env)}
                                                  sx={{ fontSize: '0.65rem', py: 0.25, px: 0.5, minWidth: 'auto' }}
                                                >
                                                  Edit
                                                </Button>
                                              </>
                                            ) : hasIssues && !isMissing ? (
                                              <>
                                                <WarningIcon sx={{ color: 'warning.main', fontSize: '1.2rem' }} />
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                  <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={async () => {
                                                      // Sync cfg.json with this environment
                                                      try {
                                                        setLoading(true);
                                                        const syncBlock = getFirstBlockName(activity);
                                                        const cfgData = await cmi5ConfigService.getCfgJson(
                                                          sourceId,
                                                          activity.tenantId,
                                                          activity.courseId,
                                                          syncBlock
                                                        );

                                                        // Find or create location for this environment (match by playerUrl)
                                                        const locationName = env.name;
                                                        const locationIndex = cfgData.locations?.findIndex(
                                                          (loc: any) => loc.url === env.playerUrl
                                                        );

                                                        if (locationIndex !== undefined && locationIndex >= 0) {
                                                          // Update existing location
                                                          cfgData.locations[locationIndex].config.DEVOPS_API_URL = env.devOpsApiUrl;
                                                          cfgData.locations[locationIndex].config.DEVOPS_GQL_URL = env.graphqlUrl;
                                                          cfgData.locations[locationIndex].config.DEVOPS_GQL_SUBSCRIPTIONS_URL = env.graphqlSubscriptionsUrl;
                                                        } else {
                                                          // Add new location
                                                          if (!cfgData.locations) cfgData.locations = [];
                                                          cfgData.locations.push({
                                                            name: locationName,
                                                            url: env.playerUrl || env.devOpsApiUrl,
                                                            config: {
                                                              DEVOPS_API_URL: env.devOpsApiUrl,
                                                              DEVOPS_GQL_URL: env.graphqlUrl,
                                                              DEVOPS_GQL_SUBSCRIPTIONS_URL: env.graphqlSubscriptionsUrl,
                                                            },
                                                          });
                                                        }

                                                        await cmi5ConfigService.updateCfgJson(
                                                          sourceId,
                                                          activity.tenantId,
                                                          activity.courseId,
                                                          cfgData,
                                                          syncBlock
                                                        );

                                                        // Re-validate
                                                        const newValidation = await validateCfgJson(activity);
                                                        setCfgValidations(prev => new Map(prev).set(activityKey, newValidation));

                                                        setSuccessMessage('cfg.json synced with environment');
                                                        setTimeout(() => setSuccessMessage(null), 5000);
                                                      } catch (err: any) {
                                                        setError(err.message || 'Failed to sync cfg.json');
                                                      } finally {
                                                        setLoading(false);
                                                      }
                                                    }}
                                                    sx={{ fontSize: '0.65rem', py: 0.25, px: 0.5, minWidth: 'auto' }}
                                                  >
                                                    Sync
                                                  </Button>
                                                  <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => handleOpenCfgEditor(activity, env)}
                                                    sx={{ fontSize: '0.65rem', py: 0.25, px: 0.5, minWidth: 'auto' }}
                                                  >
                                                    Edit
                                                  </Button>
                                                </Box>
                                              </>
                                            ) : (
                                              <>
                                                <CancelIcon sx={{ color: 'error.main', fontSize: '1.2rem' }} />
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                  <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={async () => {
                                                      // Add this environment to cfg.json
                                                      try {
                                                        setLoading(true);
                                                        const addBlock = getFirstBlockName(activity);
                                                        const cfgData = await cmi5ConfigService.getCfgJson(
                                                          sourceId,
                                                          activity.tenantId,
                                                          activity.courseId,
                                                          addBlock
                                                        );

                                                        const locationName = env.name;
                                                        if (!cfgData.locations) cfgData.locations = [];

                                                        cfgData.locations.push({
                                                          name: locationName,
                                                          url: env.playerUrl || env.devOpsApiUrl,
                                                          config: {
                                                            DEVOPS_API_URL: env.devOpsApiUrl,
                                                            DEVOPS_GQL_URL: env.graphqlUrl,
                                                            DEVOPS_GQL_SUBSCRIPTIONS_URL: env.graphqlSubscriptionsUrl,
                                                          },
                                                        });

                                                        await cmi5ConfigService.updateCfgJson(
                                                          sourceId,
                                                          activity.tenantId,
                                                          activity.courseId,
                                                          cfgData,
                                                          addBlock
                                                        );

                                                        // Re-validate
                                                        const newValidation = await validateCfgJson(activity);
                                                        setCfgValidations(prev => new Map(prev).set(activityKey, newValidation));

                                                        setSuccessMessage('Environment added to cfg.json');
                                                        setTimeout(() => setSuccessMessage(null), 5000);
                                                      } catch (err: any) {
                                                        setError(err.message || 'Failed to add environment to cfg.json');
                                                      } finally {
                                                        setLoading(false);
                                                      }
                                                    }}
                                                    sx={{ fontSize: '0.65rem', py: 0.25, px: 0.5, minWidth: 'auto' }}
                                                  >
                                                    Add
                                                  </Button>
                                                  <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => handleOpenCfgEditor(activity, env)}
                                                    sx={{ fontSize: '0.65rem', py: 0.25, px: 0.5, minWidth: 'auto' }}
                                                  >
                                                    Edit
                                                  </Button>
                                                </Box>
                                              </>
                                            )}
                                          </Box>
                                        </td>
                                      );
                                    })}
                                  </tr>}
                                  {config.aus
                                    ?.filter(au => au.rangeosScenarioName) // Only show AUs with scenarios
                                    .map((au) => {
                                      const activityMatrix = mappingMatrix.get(activityKey);
                                      const activityAvailability = scenarioAvailability.get(activityKey);

                                      // Check by scenario UUID instead of AU ID
                                      // This way, multiple AUs referencing the same scenario will all show as saved
                                      const hasSavedDefinition = au.rangeosScenarioUUID && savedScenarioDefinitions.has(au.rangeosScenarioUUID);

                                      return (
                                        <tr key={au.id}>
                                          <td style={{ padding: '8px', borderBottom: `1px solid ${theme.palette.divider}` }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="body2">
                                                  {au.title}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                                  {au.id}
                                                </Typography>
                                                {au.rangeosScenarioName && (
                                                  <Typography variant="caption" color="primary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                    {au.rangeosScenarioName}
                                                  </Typography>
                                                )}
                                              </Box>
                                              {sourceType === 's3' && hasSavedDefinition && (
                                                <Chip label="Saved" size="small" color="success" sx={{ fontSize: '0.6rem', height: '18px' }} />
                                              )}
                                            </Box>
                                          </td>
                                          {environments.map((env) => {
                                            const envMappings = activityMatrix?.get(env.id);
                                            const envAvailability = activityAvailability?.get(env.id);
                                            const hasMapping = envMappings?.get(au.id) || false;
                                            const scenarioAvailable = envAvailability?.get(au.id) || false;
                                            const isSelected = env.id === selectedEnvironment;
                                            const isImporting = importingScenarios.has(`${au.id}-${env.id}`);
                                            const isExporting = exportingScenarios.has(`${au.id}-${env.id}`);

                                            return (
                                              <td
                                                key={env.id}
                                                style={{
                                                  textAlign: 'center',
                                                  padding: '8px',
                                                  borderBottom: `1px solid ${theme.palette.divider}`,
                                                  backgroundColor: isSelected ? theme.palette.action.selected : undefined
                                                }}
                                              >
                                                {hasMapping ? (
                                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => handleManualMap(activity, au, env.id)}
                                                      sx={{ p: 0.5 }}
                                                      title="Edit mapping"
                                                    >
                                                      <EditIcon sx={{ fontSize: '1rem' }} />
                                                    </IconButton>
                                                    {sourceType === 's3' && au.rangeosScenarioUUID && (
                                                      <IconButton
                                                        size="small"
                                                        onClick={() => handleExportScenario(activity, au, env.id)}
                                                        disabled={isExporting}
                                                        title="Export scenario definition from this environment"
                                                        sx={{ p: 0.5 }}
                                                      >
                                                        {isExporting ? <CircularProgress size={14} /> : <CloudDownloadIcon sx={{ fontSize: '1rem', color: 'success.main' }} />}
                                                      </IconButton>
                                                    )}
                                                    {sourceType === 's3' && hasSavedDefinition && (
                                                      <IconButton
                                                        size="small"
                                                        onClick={() => handleImportScenario(activity, au, env.id)}
                                                        disabled={isImporting}
                                                        title="Import scenario definition to this environment"
                                                        sx={{ p: 0.5 }}
                                                      >
                                                        {isImporting ? <CircularProgress size={14} /> : <CloudUploadIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />}
                                                      </IconButton>
                                                    )}
                                                  </Box>
                                                ) : scenarioAvailable ? (
                                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                    <Button
                                                      size="small"
                                                      variant="outlined"
                                                      color="primary"
                                                      onClick={() => handleAutoMap(activity, au, env.id)}
                                                      sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
                                                    >
                                                      Auto-Map
                                                    </Button>
                                                    {sourceType === 's3' && hasSavedDefinition && (
                                                      <IconButton
                                                        size="small"
                                                        onClick={() => handleImportScenario(activity, au, env.id)}
                                                        disabled={isImporting}
                                                        title="Import scenario definition to this environment"
                                                        sx={{ p: 0.5 }}
                                                      >
                                                        {isImporting ? <CircularProgress size={14} /> : <CloudUploadIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />}
                                                      </IconButton>
                                                    )}
                                                  </Box>
                                                ) : (
                                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                    <Button
                                                      size="small"
                                                      variant="outlined"
                                                      color="secondary"
                                                      onClick={() => handleManualMap(activity, au, env.id)}
                                                      sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
                                                    >
                                                      Map
                                                    </Button>
                                                    {sourceType === 's3' && hasSavedDefinition && (
                                                      <IconButton
                                                        size="small"
                                                        onClick={() => handleImportScenario(activity, au, env.id)}
                                                        disabled={isImporting}
                                                        title="Import scenario definition to this environment"
                                                        sx={{ p: 0.5 }}
                                                      >
                                                        {isImporting ? <CircularProgress size={14} /> : <CloudUploadIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />}
                                                      </IconButton>
                                                    )}
                                                  </Box>
                                                )}
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </Box>
                          </Box>
                        )}

                      {config && config.blocks && config.blocks.length > 0 ? (
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Blocks ({config.blocks.length})
                          </Typography>
                          {config.blocks.map((block, blockIdx) => (
                            <Accordion key={blockIdx} sx={{ mb: 1 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                  <Typography variant="body1" sx={{ flexGrow: 1 }}>
                                    {block.blockName}
                                  </Typography>
                                  <Chip
                                    label={`${block.aus.length} AU${block.aus.length !== 1 ? 's' : ''}`}
                                    size="small"
                                    color="default"
                                  />
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {block.blockDescription && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                      {block.blockDescription}
                                    </Typography>
                                  )}

                                  {/* Per-block cfg.json Validation Status */}
                                  {(() => {
                                    const blockCacheKey = getCfgCacheKey(activity, block.blockName);
                                    const validation = cfgValidations.get(blockCacheKey);
                                    if (!validation) return null;

                                    return (
                                      <Box sx={{ mb: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                          <Typography variant="caption" sx={{ fontWeight: 600 }}>cfg.json Configuration</Typography>
                                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {sourceType === 'moodle' && (
                                              <Button
                                                size="small"
                                                startIcon={<UploadIcon />}
                                                onClick={() => handleExportToS3(activity)}
                                                variant="outlined"
                                                color="primary"
                                                sx={{ fontSize: '0.7rem', py: 0.25 }}
                                              >
                                                Export to S3
                                              </Button>
                                            )}
                                            <Button
                                              size="small"
                                              startIcon={<EditIcon />}
                                              onClick={() => handleOpenCfgEditor(activity, undefined, block.blockName)}
                                              variant="outlined"
                                              sx={{ fontSize: '0.7rem', py: 0.25 }}
                                            >
                                              Edit cfg.json
                                            </Button>
                                          </Box>
                                        </Box>
                                        {!validation.exists ? (
                                          <Alert severity="error" sx={{ fontSize: '0.8rem', py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                                            <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                              cfg.json Not Found
                                            </Typography>
                                            {validation.issues && validation.issues.map((issue, idx) => (
                                              <Typography key={idx} variant="caption" display="block">
                                                {issue}
                                              </Typography>
                                            ))}
                                          </Alert>
                                        ) : !validation.hasEnvironment && selectedEnvironment ? (
                                          <Alert severity="warning" sx={{ fontSize: '0.8rem', py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                                            <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                              Environment Not Configured
                                            </Typography>
                                            {validation.issues && validation.issues.map((issue, idx) => (
                                              <Typography key={idx} variant="caption" display="block">
                                                {issue}
                                              </Typography>
                                            ))}
                                          </Alert>
                                        ) : validation.environmentMatches ? (
                                          <Alert severity="success" sx={{ fontSize: '0.8rem', py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                                            <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                              cfg.json Valid
                                            </Typography>
                                            {validation.configuredLocationName && (
                                              <Typography variant="caption" display="block">
                                                Location: {validation.configuredLocationName}
                                              </Typography>
                                            )}
                                          </Alert>
                                        ) : validation.hasEnvironment && validation.issues && validation.issues.length > 0 ? (
                                          <Alert severity="warning" sx={{ fontSize: '0.8rem', py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                                            <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                              cfg.json Mismatch
                                            </Typography>
                                            {validation.issues.map((issue, idx) => (
                                              <Typography key={idx} variant="caption" display="block">
                                                {issue}
                                              </Typography>
                                            ))}
                                          </Alert>
                                        ) : validation.exists && !selectedEnvironment ? (
                                          <Alert severity="info" sx={{ fontSize: '0.8rem', py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                                            <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                              cfg.json Found
                                            </Typography>
                                            {validation.configuredLocationName && (
                                              <Typography variant="caption" display="block">
                                                Has location: {validation.configuredLocationName}
                                              </Typography>
                                            )}
                                            <Typography variant="caption" display="block">
                                              Select an environment to validate
                                            </Typography>
                                          </Alert>
                                        ) : null}

                                        {/* Per-environment status chips */}
                                        {environments.length > 0 && cfgDataCache.get(blockCacheKey) && (
                                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                            {environments.map((env) => {
                                              const blockCfgData = cfgDataCache.get(blockCacheKey);
                                              if (!blockCfgData) return null;
                                              const envValidation = validateCfgJsonForEnvironment(blockCfgData, env);
                                              const isConfigured = envValidation.hasEnvironment && envValidation.environmentMatches;
                                              const hasIssues = envValidation.issues && envValidation.issues.length > 0;
                                              const isMissing = !envValidation.hasEnvironment;

                                              return (
                                                <Chip
                                                  key={env.id}
                                                  label={env.name}
                                                  size="small"
                                                  color={isConfigured && !hasIssues ? 'success' : isMissing ? 'error' : 'warning'}
                                                  variant="outlined"
                                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                                  onClick={() => handleOpenCfgEditor(activity, env, block.blockName)}
                                                />
                                              );
                                            })}
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  })()}

                                  <List dense>
                                    {block.aus.map((au, auIdx) => (
                                      <ListItem
                                        key={au.id || auIdx}
                                        sx={{
                                          border: 1,
                                          borderColor: 'divider',
                                          borderRadius: 1,
                                          mb: 1,
                                        }}
                                      >
                                        <ListItemText
                                          primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Typography variant="body2">
                                                {au.title}
                                              </Typography>
                                              {au.mappedScenarios && au.mappedScenarios.length > 0 && (
                                                <Chip
                                                  label={`Mapped: ${au.mappedScenarios.length} scenario(s)`}
                                                  size="small"
                                                  color="primary"
                                                />
                                              )}
                                              {!au.mappedScenarios && (au.rangeosScenarioName || au.rangeosScenarioUUID) && (
                                                <Chip label="Scenario" size="small" color="success" />
                                              )}
                                              <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleEditIndividualAU(activity, au.id);
                                                }}
                                              >
                                                <EditIcon fontSize="small" />
                                              </IconButton>
                                            </Box>
                                          }
                                          secondary={
                                            <Box sx={{ mt: 0.5 }}>
                                              {/* AU ID */}
                                              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                                AU ID: {au.id}
                                              </Typography>

                                              {au.description && (
                                                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                                  {au.description}
                                                </Typography>
                                              )}

                                              {/* RC5.yaml Configuration */}
                                              {(au.rangeosScenarioName || au.rangeosScenarioUUID) && (
                                                <Box sx={{ mb: 0.5, p: 0.5, bgcolor: 'success.50', borderRadius: 0.5 }}>
                                                  <Typography variant="caption" display="block" color="success.dark" sx={{ fontWeight: 'bold' }}>
                                                    📄 RC5.yaml Definition:
                                                  </Typography>
                                                  {au.rangeosScenarioName && (
                                                    <Typography variant="caption" display="block" color="success.main">
                                                      • Scenario: {au.rangeosScenarioName}
                                                    </Typography>
                                                  )}
                                                  {au.rangeosScenarioUUID && (
                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                      • UUID: {au.rangeosScenarioUUID}
                                                    </Typography>
                                                  )}
                                                </Box>
                                              )}

                                              {/* Active AU Mappings from RangeOS Environment */}
                                              {au.mappedScenarios && au.mappedScenarios.length > 0 && (
                                                <Box sx={{ mb: 0.5, p: 0.5, bgcolor: 'primary.50', borderRadius: 0.5 }}>
                                                  <Typography variant="caption" display="block" color="primary.dark" sx={{ fontWeight: 'bold' }}>
                                                    🔗 Active AU Mapping ({au.mappingName || 'default'}):
                                                  </Typography>
                                                  <Typography variant="caption" display="block" color="primary.main">
                                                    • Scenarios: {au.mappedScenarios.join(', ')}
                                                  </Typography>
                                                </Box>
                                              )}

                                              {/* Show if no configuration exists */}
                                              {!au.rangeosScenarioName && !au.rangeosScenarioUUID && (!au.mappedScenarios || au.mappedScenarios.length === 0) && (
                                                <Box sx={{ p: 0.5, bgcolor: 'warning.50', borderRadius: 0.5 }}>
                                                  <Typography variant="caption" display="block" color="warning.dark">
                                                    ⚠️ No RC5.yaml or AU mapping configured
                                                  </Typography>
                                                </Box>
                                              )}

                                              {/* Show warning if environment is selected but no AU mapping exists */}
                                              {selectedEnvironment && !au.mappedScenarios && (au.rangeosScenarioName || au.rangeosScenarioUUID) && (
                                                <Box sx={{ p: 0.5, bgcolor: 'warning.50', borderRadius: 0.5 }}>
                                                  <Typography variant="caption" display="block" color="warning.dark">
                                                    ℹ️ Environment selected but no AU mapping found - only RC5.yaml definition exists
                                                  </Typography>
                                                </Box>
                                              )}

                                              {/* Show warning if environment is selected but AU has neither RC5 nor mapping */}
                                              {selectedEnvironment && !au.mappedScenarios && !au.rangeosScenarioName && !au.rangeosScenarioUUID && (
                                                <Box sx={{ p: 0.5, bgcolor: 'error.50', borderRadius: 0.5 }}>
                                                  <Typography variant="caption" display="block" color="error.dark">
                                                    ❌ Environment selected but no AU mapping or RC5.yaml definition exists
                                                  </Typography>
                                                </Box>
                                              )}

                                              {/* AU-Scenario Linkage Display (for S3 sources only) */}
                                              {sourceType === 's3' && s3BucketId && s3EnvironmentId && (
                                                <Box sx={{ mt: 1, p: 0.5, bgcolor: 'info.50', borderRadius: 0.5 }}>
                                                  <Typography variant="caption" display="block" color="info.dark" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                    🔗 Versioned Scenario Linkage:
                                                  </Typography>
                                                  <AUScenarioLinkageDisplay
                                                    bucketId={s3BucketId}
                                                    environmentId={s3EnvironmentId}
                                                    packageKey={activity.courseId}
                                                    auId={au.id}
                                                    auTitle={au.title}
                                                    onEdit={() => handleEditAULinkage(activity, au)}
                                                  />
                                                </Box>
                                              )}
                                            </Box>
                                          }
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Box>
                      ) : config && config.aus && config.aus.length > 0 ? (
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Assignable Units ({config.aus.length})
                          </Typography>
                          <List dense>
                            {config.aus.map((au, idx) => (
                              <ListItem
                                key={au.id || idx}
                                sx={{
                                  border: 1,
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  mb: 1,
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2">
                                        {au.title}
                                      </Typography>
                                      {au.mappedScenarios && au.mappedScenarios.length > 0 && (
                                        <Chip
                                          label={`Mapped: ${au.mappedScenarios.length} scenario(s)`}
                                          size="small"
                                          color="primary"
                                        />
                                      )}
                                      {!au.mappedScenarios && (au.rangeosScenarioName || au.rangeosScenarioUUID) && (
                                        <Chip label="Scenario" size="small" color="success" />
                                      )}
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditIndividualAU(activity, au.id);
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  }
                                  secondary={
                                    <Box sx={{ mt: 0.5 }}>
                                      {/* AU ID */}
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                        AU ID: {au.id}
                                      </Typography>

                                      {au.description && (
                                        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                          {au.description}
                                        </Typography>
                                      )}

                                      {/* RC5.yaml Configuration */}
                                      {(au.rangeosScenarioName || au.rangeosScenarioUUID) && (
                                        <Box sx={{ mb: 0.5, p: 0.5, bgcolor: 'success.50', borderRadius: 0.5 }}>
                                          <Typography variant="caption" display="block" color="success.dark" sx={{ fontWeight: 'bold' }}>
                                            📄 RC5.yaml Definition:
                                          </Typography>
                                          {au.rangeosScenarioName && (
                                            <Typography variant="caption" display="block" color="success.main">
                                              • Scenario: {au.rangeosScenarioName}
                                            </Typography>
                                          )}
                                          {au.rangeosScenarioUUID && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                              • UUID: {au.rangeosScenarioUUID}
                                            </Typography>
                                          )}
                                        </Box>
                                      )}

                                      {/* Active AU Mappings from RangeOS Environment */}
                                      {au.mappedScenarios && au.mappedScenarios.length > 0 && (
                                        <Box sx={{ mb: 0.5, p: 0.5, bgcolor: 'primary.50', borderRadius: 0.5 }}>
                                          <Typography variant="caption" display="block" color="primary.dark" sx={{ fontWeight: 'bold' }}>
                                            🔗 Active AU Mapping ({au.mappingName || 'default'}):
                                          </Typography>
                                          <Typography variant="caption" display="block" color="primary.main">
                                            • Scenarios: {au.mappedScenarios.join(', ')}
                                          </Typography>
                                        </Box>
                                      )}

                                      {/* Show if no configuration exists */}
                                      {!au.rangeosScenarioName && !au.rangeosScenarioUUID && (!au.mappedScenarios || au.mappedScenarios.length === 0) && (
                                        <Box sx={{ p: 0.5, bgcolor: 'warning.50', borderRadius: 0.5 }}>
                                          <Typography variant="caption" display="block" color="warning.dark">
                                            ⚠️ No RC5.yaml or AU mapping configured
                                          </Typography>
                                        </Box>
                                      )}

                                      {/* Show warning if environment is selected but no AU mapping exists */}
                                      {selectedEnvironment && !au.mappedScenarios && (au.rangeosScenarioName || au.rangeosScenarioUUID) && (
                                        <Box sx={{ p: 0.5, bgcolor: 'warning.50', borderRadius: 0.5 }}>
                                          <Typography variant="caption" display="block" color="warning.dark">
                                            ℹ️ Environment selected but no AU mapping found - only RC5.yaml definition exists
                                          </Typography>
                                        </Box>
                                      )}

                                      {/* Show warning if environment is selected but AU has neither RC5 nor mapping */}
                                      {selectedEnvironment && !au.mappedScenarios && !au.rangeosScenarioName && !au.rangeosScenarioUUID && (
                                        <Box sx={{ p: 0.5, bgcolor: 'error.50', borderRadius: 0.5 }}>
                                          <Typography variant="caption" display="block" color="error.dark">
                                            ❌ Environment selected but no AU mapping or RC5.yaml definition exists
                                          </Typography>
                                        </Box>
                                      )}

                                      {/* AU-Scenario Linkage Display (for S3 sources only) */}
                                      {sourceType === 's3' && s3BucketId && s3EnvironmentId && (
                                        <Box sx={{ mt: 1, p: 0.5, bgcolor: 'info.50', borderRadius: 0.5 }}>
                                          <Typography variant="caption" display="block" color="info.dark" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                            🔗 Versioned Scenario Linkage:
                                          </Typography>
                                          <AUScenarioLinkageDisplay
                                            bucketId={s3BucketId}
                                            environmentId={s3EnvironmentId}
                                            packageKey={activity.courseId}
                                            auId={au.id}
                                            auTitle={au.title}
                                            onEdit={() => handleEditAULinkage(activity, au)}
                                          />
                                        </Box>
                                      )}
                                    </Box>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      ) : (
                        <Alert severity="info">
                          No AUs found for this activity.
                        </Alert>
                      )}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Scenario Selection Dialog */}
      {scenarioDialogActivity && (selectedEnvironment || scenarioDialogEnvironment) && (() => {
        const allAus = activityConfigs.get(`${scenarioDialogActivity.tenantId}-${scenarioDialogActivity.courseId}`)?.aus || [];
        // If editing a single AU, filter to just that one
        const filteredAus = editingAU ? allAus.filter(au => au.id === editingAU) : allAus;
        // Get the environment name for display
        const envId = scenarioDialogEnvironment || selectedEnvironment;
        const envName = environments.find(e => e.id === envId)?.name;

        // Compute S3 bucket and environment IDs for logging
        const computedS3BucketId = sourceType === 'moodle' ? moodleS3BucketId : s3BucketId;
        const computedS3EnvironmentId = sourceType === 'moodle' ? scenarioDialogActivity.tenantId : s3EnvironmentId;

        console.log('[CMI5Browser] Opening ScenarioSelectionDialog with:', {
          sourceType,
          moodleS3BucketId,
          s3BucketId,
          s3EnvironmentId,
          computedS3BucketId,
          computedS3EnvironmentId,
          tenantId: scenarioDialogActivity.tenantId,
        });

        return (
          <ScenarioSelectionDialog
            open={scenarioDialogOpen}
            onClose={() => {
              setScenarioDialogOpen(false);
              setScenarioDialogActivity(null);
              setScenarioDialogAu(null);
              setScenarioDialogEnvironment(null);
              setEditingAU(null);
            }}
            aus={scenarioDialogAu ? [scenarioDialogAu] : filteredAus}
            environmentId={envId}
            environmentName={envName}
            s3BucketId={sourceType === 'moodle' ? moodleS3BucketId : s3BucketId}
            s3EnvironmentId={sourceType === 'moodle' ? scenarioDialogActivity.tenantId : s3EnvironmentId}
            onSave={async (mappings, envId) => {
              console.log('[CMI5Browser] onSave called with:', {
                mappingsCount: mappings.length,
                mappings,
                envId,
                hasScenarioDialogActivity: !!scenarioDialogActivity,
                hasScenarioDialogAu: !!scenarioDialogAu,
                hasScenarioDialogEnvironment: !!scenarioDialogEnvironment,
              });

              // Handle manual mapping from matrix
              if (scenarioDialogActivity && scenarioDialogAu && scenarioDialogEnvironment) {
                try {
                  setLoading(true);
                  const mapping = mappings[0];
                  console.log('[CMI5Browser] Processing mapping:', mapping);

                  // If this is a draft mapping, import the draft to RangeOS first
                  let scenarioIdToUse = mapping.scenarioId;
                  console.log('[CMI5Browser] Checking if draft import needed:', {
                    mappingType: mapping.mappingType,
                    hasDraftScenarioUuid: !!mapping.draftScenarioUuid,
                    s3BucketId,
                    s3EnvironmentId,
                  });
                  if (mapping.mappingType === 'draft' && mapping.draftScenarioUuid) {
                    if (!computedS3BucketId || !computedS3EnvironmentId) {
                      throw new Error('S3 bucket and environment are required for draft import');
                    }

                    console.log(`[CMI5Browser] Importing draft scenario ${mapping.draftScenarioUuid} to RangeOS before creating AU mapping`);
                    setSuccessMessage(`Importing draft scenario "${mapping.scenarioName}" to RangeOS...`);

                    // Extract activity ID from the scenarioDialogActivity
                    const activityId = parseInt(scenarioDialogActivity.courseId, 10) || undefined;

                    const importResult = await rangeosEnvironmentService.importDraftFromS3(envId, {
                      s3BucketId: computedS3BucketId,
                      s3EnvironmentId: computedS3EnvironmentId,
                      scenarioUuid: mapping.draftScenarioUuid,
                      activityId,
                    });

                    if (!importResult.success) {
                      throw new Error(importResult.message || 'Failed to import draft scenario');
                    }

                    console.log(`[CMI5Browser] Successfully imported draft. New scenario ID: ${importResult.scenarioId}`);
                    scenarioIdToUse = importResult.scenarioId;
                  }

                  await rangeosEnvironmentService.saveAUMapping(envId, {
                    auId: mapping.auId,
                    name: scenarioDialogAu.title,
                    scenarios: [scenarioIdToUse],
                  });

                  setSuccessMessage(`Successfully mapped "${scenarioDialogAu.title}" to "${mapping.scenarioName}"`);
                  setTimeout(() => setSuccessMessage(null), 5000);

                  // Reload activity config
                  await loadActivityConfig(scenarioDialogActivity, true);

                  setScenarioDialogOpen(false);
                  setScenarioDialogActivity(null);
                  setScenarioDialogAu(null);
                  setScenarioDialogEnvironment(null);
                } catch (err: any) {
                  console.error('[CMI5Browser] Error in onSave:', err);
                  console.error('[CMI5Browser] Error details:', {
                    message: err.message,
                    response: err.response?.data,
                    stack: err.stack,
                  });
                  setError(err.message || 'Failed to save manual mapping');
                } finally {
                  setLoading(false);
                }
              } else {
                console.log('[CMI5Browser] Using handleScenarioMappingSave flow');
                // Handle existing scenario mapping save flow
                handleScenarioMappingSave(mappings, envId);
              }
            }}
          />
        );
      })()}

      {/* S3 Export Dialog */}
      {s3ExportActivity && (
        <S3ExportDialog
          open={s3ExportDialogOpen}
          onClose={() => {
            setS3ExportDialogOpen(false);
            setS3ExportActivity(null);
          }}
          sourceId={sourceId}
          tenantId={s3ExportActivity.tenantId}
          courseId={s3ExportActivity.courseId}
          activityName={s3ExportActivity.activityName}
        />
      )}

      {/* S3 Batch Export Dialog */}
      <S3BatchExportDialog
        open={batchExportDialogOpen}
        onClose={() => {
          setBatchExportDialogOpen(false);
          setSelectedActivities(new Set()); // Clear selection after export
        }}
        sourceId={sourceId}
        activities={getSelectedActivitiesArray()}
      />

      {/* cfg.json Editor Dialog */}
      <Dialog
        open={cfgEditorOpen}
        onClose={() => setCfgEditorOpen(false)}
        maxWidth={cfgEditorShowDiff ? "lg" : "md"}
        fullWidth
      >
        <DialogTitle>
          {cfgEditorEnvironment ? (
            <>
              Edit cfg.json Location for {cfgEditorEnvironment.name}
              {cfgEditorActivity && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {cfgEditorActivity.activityName} (Tenant: {cfgEditorActivity.tenantId}, Course: {cfgEditorActivity.courseId})
                </Typography>
              )}
            </>
          ) : (
            <>
              Edit cfg.json
              {cfgEditorActivity && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {cfgEditorActivity.activityName} (Tenant: {cfgEditorActivity.tenantId}, Course: {cfgEditorActivity.courseId})
                </Typography>
              )}
            </>
          )}
        </DialogTitle>
        <DialogContent>
          {cfgEditorError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCfgEditorError(null)}>
              {cfgEditorError}
            </Alert>
          )}
          {cfgEditorEnvironment && (
            <Alert severity={cfgEditorShowDiff ? "warning" : "info"} sx={{ mb: 2 }}>
              <Typography variant="caption" display="block">
                {cfgEditorShowDiff ? (
                  <>Showing differences between <strong>current</strong> cfg.json and <strong>expected</strong> values for {cfgEditorEnvironment.name}.</>
                ) : (
                  <>Editing the location stanza for <strong>{cfgEditorEnvironment.name}</strong>.</>
                )}
              </Typography>
              <Typography variant="caption" display="block">
                Changes will be merged into the full cfg.json when saved.
              </Typography>
            </Alert>
          )}

          {cfgEditorShowDiff ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Line-by-line diff view */}
              <Box sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: '500px',
                overflowY: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.75rem'
              }}>
                {(() => {
                  try {
                    const current = JSON.parse(cfgEditorData);
                    const expected = JSON.parse(cfgEditorExpectedData);

                    const renderDiff = (key: string, currentVal: any, expectedVal: any, indent = 0) => {
                      const indentStr = '  '.repeat(indent);
                      const isDifferent = JSON.stringify(currentVal) !== JSON.stringify(expectedVal);

                      if (typeof currentVal === 'object' && currentVal !== null && !Array.isArray(currentVal)) {
                        return (
                          <Box key={key}>
                            <Box sx={{ px: 2, py: 0.5, bgcolor: 'action.hover' }}>
                              {indentStr}"{key}": {'{'}
                            </Box>
                            {Object.keys({...currentVal, ...expectedVal}).map(subKey => {
                              const currVal = currentVal[subKey];
                              const expVal = expectedVal[subKey];
                              return renderDiff(subKey, currVal, expVal, indent + 1);
                            })}
                            <Box sx={{ px: 2, py: 0.5, bgcolor: 'action.hover' }}>
                              {indentStr}{'}'}
                            </Box>
                          </Box>
                        );
                      }

                      return (
                        <Box
                          key={key}
                          sx={{
                            display: 'flex',
                            px: 2,
                            py: 0.5,
                            bgcolor: isDifferent ? 'warning.light' : 'transparent',
                            borderLeft: isDifferent ? '3px solid' : 'none',
                            borderLeftColor: isDifferent ? 'warning.main' : 'transparent'
                          }}
                        >
                          <Box sx={{ flex: 1, color: isDifferent ? 'error.main' : 'inherit' }}>
                            {indentStr}"{key}": {JSON.stringify(currentVal)}
                          </Box>
                          {isDifferent && (
                            <Box sx={{ flex: 1, color: 'success.main', borderLeft: 1, borderColor: 'divider', pl: 2 }}>
                              {indentStr}"{key}": {JSON.stringify(expectedVal)}
                            </Box>
                          )}
                        </Box>
                      );
                    };

                    return (
                      <Box>
                        {Object.keys({...current, ...expected}).map(key =>
                          renderDiff(key, current[key], expected[key], 0)
                        )}
                      </Box>
                    );
                  } catch (e) {
                    return (
                      <Alert severity="error" sx={{ m: 2 }}>
                        Unable to parse JSON for diff view
                      </Alert>
                    );
                  }
                })()}
              </Box>

              {/* Editable text areas */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'error.main', fontWeight: 600 }}>
                    Current (editable)
                  </Typography>
                  <TextField
                    multiline
                    rows={12}
                    fullWidth
                    value={cfgEditorData}
                    onChange={(e) => setCfgEditorData(e.target.value)}
                    placeholder="Current location configuration..."
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      '& .MuiInputBase-root': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(211, 47, 47, 0.05)'
                      }
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main', fontWeight: 600 }}>
                    Expected (read-only)
                  </Typography>
                  <TextField
                    multiline
                    rows={12}
                    fullWidth
                    value={cfgEditorExpectedData}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      '& .MuiInputBase-root': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.1)' : 'rgba(46, 125, 50, 0.05)'
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>
          ) : (
            <TextField
              multiline
              rows={20}
              fullWidth
              value={cfgEditorData}
              onChange={(e) => setCfgEditorData(e.target.value)}
              placeholder={cfgEditorEnvironment ? "Location configuration..." : "cfg.json content..."}
              sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
              helperText={
                cfgEditorEnvironment
                  ? `Edit the location configuration for ${cfgEditorEnvironment.name}. This should be a valid location object with name, url, and config fields.`
                  : "Edit the complete JSON configuration. Make sure the JSON is valid before saving."
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCfgEditorOpen(false)}>Cancel</Button>
          {cfgEditorShowDiff && (
            <Button
              onClick={() => {
                setCfgEditorData(cfgEditorExpectedData);
                setSuccessMessage('Applied expected values');
                setTimeout(() => setSuccessMessage(null), 3000);
              }}
              color="success"
              variant="outlined"
            >
              Use Expected Values
            </Button>
          )}
          <Button
            onClick={handleSaveCfgJson}
            variant="contained"
            disabled={loading}
          >
            {cfgEditorEnvironment ? `Save for ${cfgEditorEnvironment.name}` : 'Save cfg.json'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AU-Scenario Linkage Edit Dialog */}
      {sourceType === 's3' && s3BucketId && s3EnvironmentId && linkageEditActivity && linkageEditAU && (
        <AUScenarioLinkageEditDialog
          open={linkageEditDialogOpen}
          onClose={() => {
            setLinkageEditDialogOpen(false);
            setLinkageEditActivity(null);
            setLinkageEditAU(null);
          }}
          bucketId={s3BucketId}
          environmentId={s3EnvironmentId}
          packageKey={linkageEditActivity.courseId}
          auId={linkageEditAU.id}
          auTitle={linkageEditAU.title}
          onSaved={() => {
            // Optionally reload activity config to refresh display
            loadActivityConfig(linkageEditActivity, true);
          }}
        />
      )}
    </Dialog>
  );
};
